import { LocationResult } from '../types/Location';
import axios from 'axios';

interface Accommodation {
  name: string;
  address?: string;
  checkIn?: Date | null;
  checkOut?: Date | null;
}

interface TripEnhancementInput {
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  destination?: LocationResult | null;
  accommodations?: Accommodation[];
  tripType?: string;
  travelers?: number;
  budget?: string;
  interests?: string[];
}

interface TripEnhancement {
  suggestedTitle?: string;
  enhancedDescription: string;
  suggestedTags: string[];
  suggestedHighlights: string[];
  suggestedActivities: string[];
  packingTips: string[];
  localCustoms?: string[];
  weatherInfo?: string;
  budgetEstimate?: {
    min: number;
    max: number;
    currency: string;
  };
}

export const enhanceTripDescription = async (input: TripEnhancementInput): Promise<TripEnhancement> => {
  try {
    // Format dates for better context
    const formattedStartDate = input.startDate.toLocaleDateString();
    const formattedEndDate = input.endDate.toLocaleDateString();
    const tripDuration = Math.ceil((input.endDate.getTime() - input.startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Format accommodations details if available
    let accommodationsInfo = 'No accommodation information provided';
    if (input.accommodations && input.accommodations.length > 0) {
      accommodationsInfo = input.accommodations.map(acc => {
        const checkInDate = acc.checkIn ? new Date(acc.checkIn).toLocaleDateString() : 'unknown';
        const checkOutDate = acc.checkOut ? new Date(acc.checkOut).toLocaleDateString() : 'unknown';
        return `${acc.name}${acc.address ? ` (${acc.address})` : ''} from ${checkInDate} to ${checkOutDate}`;
      }).join('\n      ');
    }
    
    // Season-specific information
    const tripMonth = input.startDate.getMonth();
    const season = tripMonth >= 2 && tripMonth <= 4 ? 'Spring' : 
                  tripMonth >= 5 && tripMonth <= 7 ? 'Summer' : 
                  tripMonth >= 8 && tripMonth <= 10 ? 'Fall' : 'Winter';

    // Construct a more detailed and personalized prompt for AI
    const prompt = `Create a personalized and evocative trip description for the following journey:

      TRIP BASICS:
      - Title: ${input.title}
      - Destination: ${input.destination?.mainText || 'Not specified'}
      - Duration: ${tripDuration} days (${formattedStartDate} to ${formattedEndDate})
      - Season: ${season}
      ${input.travelers ? `- Number of travelers: ${input.travelers}` : ''}
      ${input.tripType ? `- Trip type: ${input.tripType}` : ''}
      ${input.budget ? `- Budget level: ${input.budget}` : ''}
      
      ACCOMMODATIONS:
      ${accommodationsInfo}
      
      CURRENT DESCRIPTION:
      ${input.description || 'No description provided'}
      
      TRAVELER INTERESTS:
      ${input.interests && input.interests.length > 0 ? input.interests.join(', ') : 'Not specified'}
      
      REQUIREMENTS:
      1. Create a vivid, engaging, and highly personalized trip narrative that feels tailor-made, not generic or templated
      2. Structure the description with beautiful HTML formatting including appropriate headers, paragraphs, and lists
      3. Include specific details about the destination that make it special during ${season}
      4. Reference the specific accommodations when describing areas to explore
      5. Create a 'story' that flows naturally from arrival through key highlights to departure
      6. Include practical information that genuinely helps the traveler, not generic advice
      7. Suggest specific local experiences, foods, cultural attractions unique to the destination
      8. Use a warm, enthusiastic tone that builds excitement for the journey
      9. Avoid generic travel clichés and obvious tourist information
      10. Make it concise yet comprehensive enough to provide a complete trip narrative

      OUTPUT FORMAT:
      - Carefully structured HTML with h2, h3, p, ul, li tags
      - Include sections: Introduction, What to Expect, Practical Information, Trip Highlights, Travel Tips
      - Add at least 5-8 relevant tags for the trip
      - Include 4-6 specific must-do activities or experiences
      - Provide customized packing recommendations for this specific trip, season, and destination
      - Add relevant cultural tips specific to the destination
      - Include weather information for the specific dates and location
      - If possible, suggest approximate budget in local currency`;

    // Make an API call to our server endpoint for Anthropic
    try {

      
      const response = await axios.post(
         `${process.env.REACT_APP_API_URL}/api/anthropic/trip-enhance`,
         {
            prompt,
            destination: input.destination?.mainText || 'destination',
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            season,
         },
         {
            headers: {
               'Content-Type': 'application/json',
            },
         }
      );
      
      if (response.data && response.data.enhancedDescription) {
        return response.data;
      } else {
        throw new Error('Invalid response format from AI service');
      }
    } catch (apiError) {
      console.error('API request failed, falling back to mock data:', apiError);
      
      // Fallback to mock data if the API call fails
      const mockEnhancement: TripEnhancement = {
        suggestedTitle: input.title,
        enhancedDescription: `
<h2>Journey to ${input.destination?.mainText || 'Your Destination'}</h2>

<p>Embark on an unforgettable journey to ${input.destination?.mainText || 'your destination'} for ${tripDuration} days of exploration and adventure. This carefully planned trip combines cultural experiences, local cuisine, and memorable activities to create the perfect getaway.</p>

<h3>What to Expect</h3>
<ul>
  <li>Immerse yourself in the local culture and traditions</li>
  <li>Experience authentic local cuisine</li>
  <li>Visit iconic landmarks and hidden gems</li>
  <li>Create lasting memories with unique experiences</li>
</ul>

<h3>Practical Information</h3>
<p>Duration: ${tripDuration} days<br>
Best time to visit: ${formattedStartDate} to ${formattedEndDate}<br>
Location: ${input.destination?.mainText || 'To be determined'}</p>

<h3>Trip Highlights</h3>
<ul>
  <li>Explore local markets and traditional shops</li>
  <li>Visit historical landmarks and museums</li>
  <li>Experience local festivals and events</li>
  <li>Try authentic regional cuisine</li>
  <li>Engage with local communities</li>
</ul>

<h3>Travel Tips</h3>
<ul>
  <li>Book accommodations in advance</li>
  <li>Research local transportation options</li>
  <li>Check weather forecasts closer to departure</li>
  <li>Consider local customs and dress codes</li>
  <li>Keep emergency contacts handy</li>
</ul>`,
        suggestedTags: [
          'adventure',
          'cultural',
          'sightseeing',
          'local experience',
          'photography',
          'food',
          'history',
          'travel'
        ],
        suggestedHighlights: [
          'Visit iconic landmarks',
          'Experience local cuisine',
          'Cultural immersion activities',
          'Photography opportunities',
          'Connect with locals'
        ],
        suggestedActivities: [
          'City walking tour',
          'Local cooking class',
          'Museum visits',
          'Evening entertainment',
          'Day trips to nearby attractions'
        ],
        packingTips: [
          'Weather-appropriate clothing',
          'Comfortable walking shoes',
          'Travel adapter',
          'Camera equipment',
          'First-aid essentials',
          'Language guide or translation app'
        ],
        localCustoms: [
          'Greeting customs',
          'Dining etiquette',
          'Dress code guidelines',
          'Cultural sensitivities',
          'Tipping practices'
        ],
        weatherInfo: `Typical weather for this time of year in ${input.destination?.mainText || 'your destination'} is pleasant with average temperatures ranging from 65°F to 80°F (18°C to 27°C). Light rain might occur, so pack a light raincoat or umbrella.`,
        budgetEstimate: {
          min: 1000,
          max: 2000,
          currency: 'USD'
        }
      };

      return mockEnhancement;
    }
  } catch (error) {
    console.error('Error enhancing trip description:', error);
    throw new Error('Failed to enhance trip description');
  }
}; 